import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

// Passo a passo para liberar a Groups API (API oficial da Meta) em um número.
// Aberta em nova aba a partir do erro "número não elegível" na tela de Grupos.

const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/home/';
const VERIFICACAO_EMPRESA_URL = 'https://business.facebook.com/settings/security';

const STATUS_OBA: Record<string, { titulo: string; texto: string; tom: 'info' | 'alerta' | 'ok' }> = {
  NOT_STARTED: {
    titulo: 'Solicitação ainda não feita',
    texto: 'A Meta informou que este número ainda não pediu o selo de Conta Comercial Oficial. Siga os passos abaixo.',
    tom: 'info',
  },
  PENDING: {
    titulo: 'Solicitação em análise',
    texto: 'O pedido de Conta Comercial Oficial já foi enviado e está em análise pela Meta. Aguarde a notificação de resultado e tente criar o grupo novamente depois da aprovação.',
    tom: 'info',
  },
  REJECTED: {
    titulo: 'Solicitação recusada',
    texto: 'A Meta recusou o pedido de Conta Comercial Oficial. É preciso esperar 30 dias para enviar outro. Use esse tempo para reforçar a presença da marca (veja o passo 5).',
    tom: 'alerta',
  },
  APPROVED: {
    titulo: 'Conta Comercial Oficial aprovada',
    texto: 'O número já tem o selo. Se o erro continua, confira os passos 4 e 6 (30 dias na plataforma e número sem coexistência). Persistindo, fale com o suporte do SinTicket.',
    tom: 'ok',
  },
};

const LinkExterno: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:underline"
  >
    {children}
    <ExternalLink className="h-3.5 w-3.5" />
  </a>
);

const Passo: React.FC<{ numero: number; titulo: string; children: React.ReactNode }> = ({ numero, titulo, children }) => (
  <section className="flex gap-4">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-600 text-sm font-bold text-white">
      {numero}
    </div>
    <div className="min-w-0 space-y-2">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{titulo}</h2>
      {children}
    </div>
  </section>
);

const AjudaMetaGrupos: React.FC = () => {
  const [params] = useSearchParams();
  const status = STATUS_OBA[(params.get('status') || '').toUpperCase()];

  const corStatus = status?.tom === 'alerta'
    ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100'
    : status?.tom === 'ok'
      ? 'border-green-300 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-900/20 dark:text-green-100'
      : 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-100';
  const IconeStatus = status?.tom === 'alerta' ? AlertTriangle : status?.tom === 'ok' ? CheckCircle2 : Info;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 px-4 py-12">
      <div className="mx-auto max-w-3xl rounded-lg bg-white p-6 shadow-md dark:bg-slate-800 md:p-12">
        <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
          Como liberar grupos na API oficial do WhatsApp
        </h1>
        <p className="mb-8 text-gray-600 dark:text-gray-300">
          A Meta só permite criar grupos pela API oficial em números com <strong>Conta Comercial Oficial</strong>{' '}
          (o selo azul de verificado). Esse selo é pedido no painel da Meta pelo dono do número — o SinTicket não
          consegue fazer isso por você. Siga os passos abaixo.
        </p>

        {status && (
          <div className={`mb-8 flex gap-3 rounded-lg border p-4 ${corStatus}`}>
            <IconeStatus className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Situação do seu número: {status.titulo}</p>
              <p className="text-sm">{status.texto}</p>
            </div>
          </div>
        )}

        <div className="space-y-8 text-gray-700 dark:text-gray-300">
          <Passo numero={1} titulo="Verifique a empresa no Meta Business">
            <p>
              O portfólio empresarial dono do número precisa estar verificado (CNPJ e documentos da empresa).
            </p>
            <p>
              Acesse <LinkExterno href={VERIFICACAO_EMPRESA_URL}>Configurações do negócio → Central de segurança</LinkExterno>{' '}
              e confira se aparece <em>"Verificado"</em> em <em>Verificação da empresa</em>. Se não, clique em{' '}
              <em>Iniciar verificação</em> e envie os documentos pedidos.
            </p>
          </Passo>

          <Passo numero={2} titulo="Confirme que o nome de exibição está aprovado">
            <p>
              No <LinkExterno href={WHATSAPP_MANAGER_URL}>Gerenciador do WhatsApp</LinkExterno>, abra{' '}
              <em>Números de telefone</em> e veja a coluna <em>Nome de exibição</em>. O status precisa estar{' '}
              <em>Aprovado</em>. Se estiver pendente ou recusado, ajuste o nome para que corresponda à marca da empresa.
            </p>
          </Passo>

          <Passo numero={3} titulo="Ative a verificação em duas etapas do número">
            <p>
              Ainda em <em>Números de telefone</em>, clique no número → aba <em>Configurações</em> →{' '}
              <em>Verificação em duas etapas</em> e cadastre um PIN de 6 dígitos. Guarde esse PIN em local seguro.
            </p>
          </Passo>

          <Passo numero={4} titulo="Aguarde 30 dias na plataforma">
            <p>
              A Meta exige que a empresa esteja usando a API oficial (WhatsApp Business Platform) há pelo menos{' '}
              <strong>30 dias</strong> antes de pedir o selo. Números recém-conectados precisam aguardar esse prazo.
            </p>
          </Passo>

          <Passo numero={5} titulo="Solicite a Conta Comercial Oficial">
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                No <LinkExterno href={WHATSAPP_MANAGER_URL}>Gerenciador do WhatsApp</LinkExterno>, vá em{' '}
                <em>Visão geral</em> e clique no número.
              </li>
              <li>
                Abra <em>Perfil</em> e, em <em>Conta comercial oficial</em>, clique em <em>Enviar solicitação</em>.
              </li>
              <li>
                Informe links que mostrem que a marca é conhecida: site oficial, matérias em jornais ou portais,
                perfis em redes sociais. Links pagos ou promocionais não contam.
              </li>
            </ol>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              A Meta avisa por notificação quando terminar a análise. Se recusar, só é possível pedir de novo após 30 dias.
              Se a opção <em>Enviar solicitação</em> não aparecer mesmo cumprindo os requisitos, abra um chamado no{' '}
              <LinkExterno href="https://business.facebook.com/business-support-home/">Suporte da Meta</LinkExterno>.
            </p>
          </Passo>

          <Passo numero={6} titulo="Use um número exclusivo da API oficial">
            <p>
              Números em <strong>coexistência</strong> (que também estão ativos no aplicativo WhatsApp Business do celular)
              e números de teste da Meta não podem usar grupos pela API oficial. Para grupos, use um número que esteja
              somente na API oficial.
            </p>
          </Passo>

          <Passo numero={7} titulo="Tente criar o grupo novamente">
            <p>
              Assim que o selo for aprovado, volte à tela <em>Grupos</em> do SinTicket e crie o grupo de novo. Enquanto
              isso, você pode usar grupos por uma conexão não oficial (Uazapi), se a sua empresa tiver uma.
            </p>
          </Passo>
        </div>

        <p className="mt-10 border-t pt-6 text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
          Referência: documentação oficial da Meta sobre{' '}
          <LinkExterno href="https://developers.facebook.com/documentation/business-messaging/whatsapp/official-business-accounts">
            Contas Comerciais Oficiais
          </LinkExterno>{' '}
          e{' '}
          <LinkExterno href="https://developers.facebook.com/documentation/business-messaging/whatsapp/groups/get-started">
            Groups API
          </LinkExterno>
          .
        </p>
      </div>
    </div>
  );
};

export default AjudaMetaGrupos;
